export const productLearningSources = [
  { label: 'CFA Institute｜債券價格與殖利率', url: 'https://www.cfainstitute.org/insights/professional-learning/refresher-readings/2026/fixed-income-bond-valuation-prices-and-yields' },
  { label: 'SEC｜槓桿與反向 ETF 的每日目標', url: 'https://www.investor.gov/introduction-investing/general-resources/news-alerts/alerts-bulletins/investor-alerts/sec' },
  { label: 'CFA Institute｜主動報酬與追蹤誤差', url: 'https://www.cfainstitute.org/insights/professional-learning/refresher-readings/2026/using-multifactor-models' },
];

const finite = value => typeof value === 'number' && Number.isFinite(value);
const outputNumber = value => Number.isFinite(value) ? (value === 0 ? 0 : value) : null;

/**
 * Price an invented, noncallable bond exactly on a coupon date before its next
 * annual period: coupon cash flows arrive at each year-end, principal at maturity.
 * Rate arguments use percentages, e.g. 3 means 3%. No accrued interest, default,
 * tax, fees or coupon reinvestment is modeled. All years share one discount yield.
 * Teaching domain: face > 0, integer years 1–30, coupon 0–100%, yield > -100% to 100%.
 */
export function priceAnnualBond(parameters = {}) {
  const { faceValue, couponRate, years, marketYield } = parameters || {};
  if (!finite(faceValue) || faceValue <= 0 || !finite(couponRate) || couponRate < 0 || couponRate > 100
    || !Number.isInteger(years) || years < 1 || years > 30
    || !finite(marketYield) || marketYield <= -100 || marketYield > 100) return null;
  const coupon = faceValue * (couponRate / 100);
  if (marketYield === 0) return outputNumber(faceValue + coupon * years);
  const discountFactor = 1 + marketYield / 100;
  let price = 0;
  for (let year = 1; year <= years; year += 1) {
    price += coupon / discountFactor ** year;
  }
  price += faceValue / discountFactor ** years;
  return outputNumber(price);
}

/**
 * Daily-reset toy path, starting at 100. Every daily target is achieved exactly;
 * fees, financing, trading frictions, distributions and termination rules are absent.
 * A modeled fund that reaches zero stays there, without new external capital.
 * Returns, including output returns, use percentages; day 0 is included in points.
 */
export function dailyLeveragedPath(dailyReturns, multiple) {
  if (!Array.isArray(dailyReturns) || dailyReturns.length < 1 || dailyReturns.length > 60
    || ![-1, 1, 2, 3].includes(multiple)) return null;
  let indexValue = 100;
  let fundValue = 100;
  const points = [{ day: 0, indexValue, fundValue }];
  for (let day = 0; day < dailyReturns.length; day += 1) {
    const dailyReturn = dailyReturns[day];
    if (!finite(dailyReturn) || dailyReturn <= -100) return null;
    const leveragedReturn = dailyReturn * multiple;
    if (!finite(leveragedReturn) || leveragedReturn < -100) return null;
    indexValue *= 1 + dailyReturn / 100;
    fundValue *= 1 + leveragedReturn / 100;
    if (!finite(indexValue) || !finite(fundValue)) return null;
    points.push({ day: day + 1, indexValue, fundValue });
  }
  const indexReturn = outputNumber((indexValue / 100 - 1) * 100);
  const fundReturn = outputNumber((fundValue / 100 - 1) * 100);
  const naiveReturn = outputNumber(indexReturn * multiple);
  if (indexReturn === null || fundReturn === null || naiveReturn === null) return null;
  return { points, indexReturn, fundReturn, naiveReturn };
}

/**
 * All observations must have the same period frequency and calculation basis.
 * Differences are fund minus benchmark, in percentage points. meanDifference is
 * the arithmetic mean of those period differences, NOT compounded tracking difference.
 * trackingError is their sample standard deviation (n - 1), with no annualization.
 */
export function trackingStatistics(fundReturns, benchmarkReturns) {
  if (!Array.isArray(fundReturns) || !Array.isArray(benchmarkReturns)
    || fundReturns.length !== benchmarkReturns.length || fundReturns.length < 2) return null;
  const differences = [];
  let meanDifference = 0;
  let sumSquaredDeviations = 0;
  for (let index = 0; index < fundReturns.length; index += 1) {
    const fundReturn = fundReturns[index];
    const benchmarkReturn = benchmarkReturns[index];
    if (!finite(fundReturn) || fundReturn < -100 || !finite(benchmarkReturn) || benchmarkReturn < -100) return null;
    const difference = fundReturn - benchmarkReturn;
    if (!finite(difference)) return null;
    differences.push(difference === 0 ? 0 : difference);
    // Welford's recurrence avoids squaring raw return differences before centering.
    const delta = difference - meanDifference;
    meanDifference += delta / (index + 1);
    sumSquaredDeviations += delta * (difference - meanDifference);
    if (!finite(meanDifference) || !finite(sumSquaredDeviations)) return null;
  }
  const trackingError = outputNumber(Math.sqrt(Math.max(0, sumSquaredDeviations / (differences.length - 1))));
  if (trackingError === null) return null;
  return { differences, meanDifference: outputNumber(meanDifference), trackingError };
}
